/* eslint-disable react/jsx-key */
import * as Yup from 'yup';
import { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { LoadingButton } from '@mui/lab';
import {
  Autocomplete,
  Box,
  Chip,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Stack,
  TextField,
 Checkbox, Switch } from '@mui/material';
// components
import TextMaxLine from '../../../components/TextMaxLine';
import { FormProvider, RHFSelect, RHFTextField } from '../../../components/hook-form';
import axiosInstance from 'src/utils/axios';
import { APIS } from 'src/utils/serviceUrls';
import { Course, CourseCreate, CourseLevelsEnum } from 'src/@types/Course';
import { InstructorType } from 'src/sections/dashboard/instructors/types';
import { Track } from 'src/@types/tracks';
import { Category } from 'src/@types/Category';
import { UploadAvatar, UploadMultiFile } from 'src/components/upload';
import { Package } from 'src/@types/package';


// ----------------------------------------------------------------------

type Props = {
  isEdit: boolean;
  currentCourse?: Course;
  handleClose(): void;
};

export default function CourseNewEditForm({ isEdit, currentCourse, handleClose }: Props) {
  const [instructors, setInstructors] = useState<InstructorType[]>([]);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [packages, setPackages] = useState<Package[]>([]);
  const [selectedPathTrack, setSelectedPathTrack] = useState<Track[]>([]);
  const expression = /^(ftp|http|https):\/\/[^ "]+$/;
  
  const { enqueueSnackbar } = useSnackbar();
  const NewCategorySchema = Yup.object().shape({
    name: Yup.string().min(3).required('Course title is required'),
    category: Yup.string().required('category is required'),
    certificateUrl: Yup.string(),
    briefVideo: Yup.string()
      .min(3)
      .required('brief Video Url title is required')
      .matches(expression, 'briefVideo Url must be a valid url'),
    description: Yup.string().min(3).required('description is Required'),
    instructor: Yup.string().required('instructor is required'),
    track: Yup.string().required('track is required'),
    active: Yup.boolean(),
    level: Yup.string().required('Course Level is Required'),
    package: Yup.string().required('package is Required'),
    passScore: Yup.number().min(0).max(100),
    slug: Yup.string(),
    order: Yup.number(),
    // A paid course (isFree = false) must carry a positive EGP price AND a
    // positive USD price. The backend treats the EGP `price` as the canonical
    // "is it paid" signal (price === 0 -> "package is free" rejection, parity
    // with tracks/packages), and charges price_usd*50 for non-EG buyers — so a
    // 0 in either field makes the course unsellable / charged as $0. Free
    // courses skip payment entirely and are exempt.
    price: Yup.number()
      .min(0)
      .when('isFree', {
        is: (val: boolean) => !val,
        then: Yup.number().moreThan(
          0,
          'A paid course needs an EGP price greater than 0 (or mark it as Free).',
        ),
      }),
    price_usd: Yup.number()
      .min(0)
      .when('isFree', {
        is: (val: boolean) => !val,
        then: Yup.number().moreThan(
          0,
          'A paid course needs a USD price greater than 0 (or mark it as Free).',
        ),
      }),
    seo_title: Yup.string(),
    seo_title_ar: Yup.string(),
    seo_description: Yup.string(),
    seo_description_ar: Yup.string(),
    keywords: Yup.array().min(1),
    icon: Yup.string(),
    image: Yup.string(),
  });

  const defaultValues: CourseCreate = useMemo(
    () => ({
      name: currentCourse?.name || '',
      name_ar: currentCourse?.name_ar || '',
      category: currentCourse?.category?._id || currentCourse?.track?.category?._id || '',
      certificateUrl: currentCourse?.certificateUrl || '',
      briefVideo: currentCourse?.briefVideo || '',
      image: currentCourse?.image || '',
      icon: currentCourse?.icon || '',
      description: currentCourse?.description || '',
      description_ar: currentCourse?.description_ar || '',
      duration: currentCourse?.duration || 0,
      instructor: currentCourse?.instructor?._id || '',
      keywords: currentCourse?.keywords || [],
      keywords_ar: currentCourse?.keywords_ar || [],
      track: currentCourse?.track?._id || '',
      active: currentCourse?.active || false,
      isFree: currentCourse?.isFree || false,
      level: currentCourse?.level || CourseLevelsEnum.BASIC,
      sections: currentCourse?.sections || [],
      package: currentCourse?.package?._id || '',
      passScore: currentCourse?.passScore ?? 85,
      slug: currentCourse?.slug || '',
      order: currentCourse?.order || 0,
      price: currentCourse?.price || 0,
      price_usd: currentCourse?.price_usd || 0,
      seo_title: currentCourse?.seo_title || '',
      seo_title_ar: currentCourse?.seo_title_ar || '',
      seo_description: currentCourse?.seo_description || '',
      seo_description_ar: currentCourse?.seo_description_ar || '',
    }),
    [currentCourse]
  );
   const uploadImage = async (file: File) => {
     const form = new FormData();
     form.append('file', file);
     const response = await axiosInstance.post(APIS.UPLOAD.MEDIA, form);
     return response.data;
   };

  const methods = useForm<CourseCreate>({
    resolver: yupResolver(NewCategorySchema),
    defaultValues,
  });

  const {
    reset,
    watch,
    handleSubmit,
    trigger,
    setValue,
    formState: { isSubmitting, errors },
  } = methods;
  const values = watch();

  useEffect(() => {
    if (isEdit && currentCourse) {
      reset(defaultValues);
    }
    if (!isEdit) {
      reset(defaultValues);
    }
  }, [isEdit, currentCourse]);

  const onSubmit = async (data: CourseCreate) => {
    if(!data.description_ar) delete data?.description_ar
    if (!data.name_ar) delete data?.name_ar;
    if (!data.keywords_ar) delete data?.keywords_ar;
    if (!data.slug) delete data?.slug;
    if (!data.seo_title) delete data?.seo_title;
    if (!data.seo_title_ar) delete data?.seo_title_ar;
    if (!data.seo_description) delete data?.seo_description;
    if (!data.seo_description_ar) delete data?.seo_description_ar;
    // title/title_ar are required by the API but not collected in the form —
    // derive them from the course name.
    const titleFields = {
      title: (data as any).title || data.name,
      title_ar: (data as any).title_ar || data.name_ar || data.name,
    };
    try {
      if (currentCourse)
        await axiosInstance.patch(APIS.COURSES.PATCH(currentCourse.id as string), {
          ...data,
          ...titleFields,
          rating: currentCourse?.rating,
          duration: 0,
        });
      else
        await axiosInstance.post(APIS.COURSES.ADD, {
          ...data,
          ...titleFields,
          rating: 1,
          duration: 0,
        });
      reset();
      enqueueSnackbar(!isEdit ? 'Create success!' : 'Update success!');
      handleClose();
    } catch (err) {
      if (err?.message) {
        enqueueSnackbar(err?.message, { variant: 'error' });
      }

      if (err?.messages.length && err?.messages[0]) {
        enqueueSnackbar(err?.messages.length && err?.messages[0], { variant: 'error' });
      } else {
        enqueueSnackbar(!isEdit ? 'Unable to create Course!' : 'Unable to edit Course', {
          variant: 'error',
        });
      }
    }
  };
  // react-hook-form calls THIS when submit is blocked by validation. The
  // keywords field is a custom control with no ref to focus, so a failed
  // validation used to no-op silently ("Save Changes does nothing"). Surface
  // the first validation error so the admin knows what to fix.
  const onInvalid = (formErrors: any) => {
    const first: any = Object.values(formErrors || {})[0];
    const msg =
      (first && (first.message as string)) ||
      'Please fill in all required fields before saving.';
    enqueueSnackbar(msg, { variant: 'error' });
  };
  const getListingOptions = async () => {
    setInstructors((await axiosInstance.get(APIS.INSTRUCTORS.LIST)).data.data);
    setCategories((await axiosInstance.get(APIS.CATEGORIES.LIST)).data.data);
    setPackages((await axiosInstance.get(APIS.PACKAGES.LIST))?.data?.data);
   
  };
  useEffect(() => {
    if (currentCourse?.category) {
      getSelectedTrack(currentCourse.category._id as string,false);
    }
  }, [currentCourse,categories]);
  const onUploadCertificate = async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    const response = await axiosInstance.post(APIS.UPLOAD.DOCUMENT, form);
    return response.data;
  };

  useEffect(() => {
    getListingOptions();
  }, []);
  const getSelectedTrack = (id: string,isChange:boolean) => {
    const path = categories.filter((item) => item._id == id);
    setSelectedPathTrack(path?.[0]?.tracks as Track[]);
    if (isChange)
    {
       setValue('track', '');
     }
  };

  return (
    <Stack height={'100%'}>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit, onInvalid)}>
        <TextMaxLine marginBottom={'20px'} marginTop={'20px'} fontSize={'22px'}>
          {isEdit ? ' Edit Course' : ' Create Course'}
        </TextMaxLine>
        <Stack direction={'column'} justifyContent={'space-between'} height={'90%'}>
          <Stack direction={'row'} justifyContent={'space-between'}>
            <UploadAvatar
              label="Course Icon"
              accept={{ 'image/*': [] }}
              error={errors.icon ? true : false}
              file={values.icon}
              onDropAccepted={async (file) => {
                let response = await uploadImage(file[0]);
                setValue('icon', response?.url);
              }}
            />
            <UploadAvatar
              label="Course Image"
              accept={{ 'image/*': [] }}
              error={errors.image ? true : false}
              file={values.image}
              onDropAccepted={async (file) => {
                let response = await uploadImage(file[0]);
                setValue('image', response?.url);
              }}
            />
          </Stack>
          <Stack direction={'column'} spacing={5} overflow={'auto'}>
            <RHFTextField
              style={{ marginTop: '10px' }}
              fullWidth
              name="briefVideo"
              label="brief Video url"
            />

            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 5,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              <RHFTextField fullWidth name="name" label="English Course Name" />
              <RHFTextField fullWidth name="name_ar" label="Arabic Course Name" />
              <RHFTextField fullWidth name="slug" label="Slug (URL)" />
              <RHFTextField fullWidth type="number" name="price" label="Price (EGP)" />
              <RHFTextField fullWidth type="number" name="price_usd" label="Price (USD)" />
              <RHFTextField fullWidth type="number" name="order" label="Order" />
              <RHFSelect
                onChange={(event) => {
                  setValue('category', event.target.value);
                  getSelectedTrack(event.target.value,true);
                }}
                name="category"
              >
                <option key={'default'} value={''} disabled>
                  {'Path'}
                </option>
                {categories?.map((option: Category) => (
                  <option key={option?._id} value={option?._id}>
                    {option.title}
                  </option>
                ))}
              </RHFSelect>
              <RHFSelect name="instructor">
                <option key={'default'} value={''} disabled>
                  {'Instructor'}
                </option>
                {instructors?.map((option: InstructorType) => (
                  <option key={option?._id} value={option?._id}>
                    {option.email ? `${option.name} — ${option.email}` : option.name}
                  </option>
                ))}
              </RHFSelect>
              <RHFSelect name="package">
                <option key={'default'} value={''} disabled>
                  {'package'}
                </option>
                {packages?.map((option: Package) => (
                  <option key={option?._id} value={option?._id}>
                    {option.name}
                  </option>
                ))}
              </RHFSelect>
              <RHFSelect name="track">
                <option key={'default'} value={''} disabled>
                  {'Track'}
                </option>
                {selectedPathTrack?.map((option: Track) => (
                  <option key={option?._id} value={option?._id}>
                    {option.title}
                  </option>
                ))}
              </RHFSelect>

              <FormControl>
                <FormLabel id="">Is Course Free</FormLabel>
                <FormControlLabel
                  checked={values.isFree}
                  onChange={(e: any) => {
                    console.log('Eee', e.target.checked);
                    setValue('isFree', !values.isFree);
                  }}
                  label="Free Course"
                  control={<Checkbox />}
                />
              </FormControl>

              <FormControl>
                <FormLabel id="">Visibility</FormLabel>
                <FormControlLabel
                  checked={!!values.active}
                  onChange={() => setValue('active', !values.active)}
                  label={values.active ? 'Published — students can see it' : 'Draft — hidden from students'}
                  control={<Switch />}
                />
              </FormControl>
            </Box>
            <RHFSelect name="level" label="Course Level" placeholder="Course Level">
              <option value="" />
              {Object.values(CourseLevelsEnum).map((value: string) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </RHFSelect>
            <UploadMultiFile
              error={
                errors.certificateUrl && (values.certificateUrl == '' || !values.certificateUrl)
                  ? true
                  : false
              }
              disableControl={true}
              accept={{ 'application/pdf': ['application/pdf'] }}
              files={values.certificateUrl ? [values.certificateUrl] : []}
              maxFiles={1}
              label={'Select Or Drop Certificate Url'}
              onDropAccepted={async (file) => {
                const response = await onUploadCertificate(file[0]);
                setValue('certificateUrl', response.url);
              }}
            />
            {errors.certificateUrl && (values.certificateUrl == '' || !values.certificateUrl) && (
              <FormHelperText error sx={{ px: 2, textAlign: 'start' }}>
                {errors.certificateUrl.message}
              </FormHelperText>
            )}

            <RHFTextField
              type="number"
              name="passScore"
              label="Pass score (%) — min final score to be certificate-eligible"
            />

            <RHFTextField
              multiline
              minRows={2}
              name="description"
              maxRows={3}
              label="English Description"
            />
            <RHFTextField
              multiline
              minRows={2}
              name="description_ar"
              maxRows={3}
              label="Arabic Description"
            />
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 5,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              <RHFTextField fullWidth name="seo_title" label="SEO Title (English)" />
              <RHFTextField fullWidth name="seo_title_ar" label="SEO Title (Arabic)" />
            </Box>
            <RHFTextField
              multiline
              minRows={2}
              maxRows={3}
              name="seo_description"
              label="SEO Description (English)"
            />
            <RHFTextField
              multiline
              minRows={2}
              maxRows={3}
              name="seo_description_ar"
              label="SEO Description (Arabic)"
            />
            <div>
              <Autocomplete
                multiple
                id="keywords"
                options={[]}
                value={values.keywords}
                freeSolo
                renderTags={(value: string[], getTagProps) =>
                  value.map((option: string, index: number) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      onDelete={(e) => {
                        values.keywords.splice(index, 1);
                        setValue('keywords', values.keywords);
                      }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={errors.keywords && values.keywords.length < 1 ? true : false}
                    name={'keywords'}
                    onKeyDown={(e) => {
                      let event = e as any;
                      if (e.key === 'Enter') {
                        setValue('keywords', [...values.keywords, event?.target?.value as any]);
                      }
                      if (e.key === 'Backspace') {
                        const value = values?.keywords;
                        value?.pop();

                        setValue('keywords', value);
                      }
                    }}
                    variant="outlined"
                    label="English Keywords"
                    placeholder="Keywords"
                    maxRows={3}
                  />
                )}
              />
              {values.keywords.length == 0 && (
                <span style={{ color: '#FF4842', fontSize: '0.75rem', marginTop: '5px' }}>
                  {errors?.keywords?.message as any}
                </span>
              )}
            </div>
            <div>
              <Autocomplete
                multiple
                id="keywords_ar"
                options={[]}
                value={values?.keywords_ar??[]}
                freeSolo
                renderTags={(value: string[], getTagProps) =>
                  value.map((option: string, index: number) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      onDelete={(e) => {
                        values?.keywords_ar?.splice(index, 1);
                        setValue('keywords_ar', values?.keywords_ar);
                      }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name={'keywords_ar'}
                    onKeyDown={(e) => {
                      let event = e as any;
                      if (e.key === 'Enter') {
                        setValue('keywords_ar', values.keywords_ar?[...values.keywords_ar, event?.target?.value as any]:[event?.target?.value as any]);
                      }
                      if (e.key === 'Backspace') {
                        const value = values?.keywords;
                        value?.pop();

                        setValue('keywords_ar', value);
                      }
                    }}
                    variant="outlined"
                    label="Arabic Keywords"
                    placeholder="Keywords"
                    maxRows={3}
                  />
                )}
              />
            </div>
          </Stack>

          <Stack alignItems="flex-end" sx={{ mt: 3 }}>
            <LoadingButton
              style={{ width: '200px', height: '50px' }}
              type="submit"
              variant="contained"
              loading={isSubmitting}
            >
              {!isEdit ? 'Create Course' : 'Save Changes'}
            </LoadingButton>
          </Stack>
        </Stack>
      </FormProvider>
    </Stack>
  );
}
